import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import eventosGanttService from '../services/eventosGanttService';
import kanbanService from '../services/kanbanService';
import usuariosService from '../services/usuariosService';
import onebaseService, { equipoService } from '../services/onebaseService';
import './TareaEventoModal.css';

/**
 * Modal para crear/editar tareas y subtareas del Gantt
 */
const TareaEventoModal = ({ isOpen, onClose, eventoId, actividadId, tarea = null, onSave }) => {
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    usuario_asignado: '',
    equipo_asignado: '',
    estado_kanban: '',
    fecha_inicio: '',
    fecha_termino: '',
    horas_estimadas: '',
    porcentaje_avance: 0,
    orden: 0,
    tarea_padre: '',
    presupuesto_costo_recursos: [],  // Ahora es un array
  });

  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [tareasPadre, setTareasPadre] = useState([]);
  const [tableros, setTableros] = useState([]);
  const [estadosKanban, setEstadosKanban] = useState([]);
  const [tableroSeleccionado, setTableroSeleccionado] = useState('');
  const [loadingTableros, setLoadingTableros] = useState(false);
  const [actividades, setActividades] = useState([]);
  const [loadingActividades, setLoadingActividades] = useState(false);
  const [recursos, setRecursos] = useState([]);
  const [loadingRecursos, setLoadingRecursos] = useState(false);
  const [actividadSeleccionadaData, setActividadSeleccionadaData] = useState(null);

  // Estados por defecto (si no hay tablero seleccionado)
  const ESTADOS_DEFECTO = [
    { value: 'por_hacer', label: 'Por Hacer' },
    { value: 'en_progreso', label: 'En Progreso' },
    { value: 'completado', label: 'Completado' },
  ];

  useEffect(() => {
    if (isOpen) {
      cargarUsuarios();
      cargarEquipos();
      cargarActividades();
      cargarTareasPadre();
      cargarTableros();
    }
  }, [isOpen]);

  // Cargar estados del tablero cuando se selecciona uno
  useEffect(() => {
    console.log('🎯 useEffect tableroSeleccionado:', tableroSeleccionado);
    if (tableroSeleccionado) {
      cargarEstadosDelTablero(tableroSeleccionado);
    } else {
      setEstadosKanban([]);
    }
  }, [tableroSeleccionado]);

  // Cargar recursos y datos de la actividad cuando se selecciona una actividad
  useEffect(() => {
    if (formData.actividad) {
      cargarRecursos(formData.actividad);
      // Encontrar la actividad seleccionada en la lista de actividades
      const actividadEncontrada = actividades.find(a => a.id === parseInt(formData.actividad));
      setActividadSeleccionadaData(actividadEncontrada || null);
    } else {
      setRecursos([]);
      setActividadSeleccionadaData(null);
    }
  }, [formData.actividad, actividades]);

  useEffect(() => {
    if (tarea) {
      console.log('📝 Cargando tarea para editar:', tarea);
      console.log('🎯 Estado Kanban:', tarea.estado_kanban);
      console.log('📊 Estado Kanban Data:', tarea.estado_kanban_data);

      setFormData({
        titulo: tarea.titulo || '',
        descripcion: tarea.descripcion || '',
        actividad: tarea.actividad || '',
        usuario_asignado: tarea.usuario_asignado || '',
        equipo_asignado: tarea.equipo_asignado || '',
        estado_kanban: tarea.estado_kanban || '',
        fecha_inicio: tarea.fecha_inicio || '',
        fecha_termino: tarea.fecha_termino || '',
        horas_estimadas: tarea.horas_estimadas || '',
        porcentaje_avance: tarea.porcentaje_avance || 0,
        orden: tarea.orden || 0,
        tarea_padre: tarea.tarea_padre || '',
        presupuesto_costo_recursos: Array.isArray(tarea.presupuesto_costo_recursos) ? tarea.presupuesto_costo_recursos : [],
      });

      // Si la tarea tiene estado_kanban, cargar el tablero asociado
      // El campo puede ser 'tablero' o 'board' dependiendo del serializer
      const tableroId = tarea.estado_kanban_data?.tablero || tarea.estado_kanban_data?.board;

      if (tableroId) {
        console.log('✅ Tablero encontrado:', tableroId);
        setTableroSeleccionado(tableroId);
      } else if (tarea.estado_kanban) {
        // Si tiene estado_kanban pero no está el tablero en estado_kanban_data
        console.log('⚠️ Tiene estado_kanban pero no tablero en data');
        // El tablero se cargará cuando se carguen todos los estados
      } else {
        console.log('ℹ️ No hay estado kanban asociado');
      }
    } else {
      setFormData({
        titulo: '',
        descripcion: '',
        actividad: actividadId || '', // Usar actividadId si está disponible
        usuario_asignado: '',
        equipo_asignado: '',
        estado_kanban: '',
        fecha_inicio: '',
        fecha_termino: '',
        horas_estimadas: '',
        porcentaje_avance: 0,
        orden: 0,
        tarea_padre: '',
        presupuesto_costo_recursos: [],
      });
    }
  }, [tarea, isOpen, actividadId]);

  const cargarUsuarios = async () => {
    try {
      const response = await usuariosService.getUsuarios();
      // El servicio retorna { success, count, usuarios }
      const usuariosData = response.usuarios || response.data || [];
      setUsuarios(usuariosData);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      setUsuarios([]);
    }
  };

  const cargarEquipos = async () => {
    try {
      const cuentaId = parseInt(localStorage.getItem('cuenta_id'));
      const response = await equipoService.getSimple({ cuenta_id: cuentaId, is_active: true });
      const equiposData = response.data || [];
      setEquipos(equiposData);
    } catch (error) {
      console.error('Error al cargar equipos:', error);
      setEquipos([]);
    }
  };

  const cargarActividades = async () => {
    try {
      if (!eventoId) return;
      setLoadingActividades(true);
      const response = await eventosGanttService.actividades.list(eventoId);
      const actividadesData = Array.isArray(response.data) ? response.data : (response.data.results || response.data || []);
      setActividades(actividadesData);
    } catch (error) {
      console.error('Error al cargar actividades:', error);
      setActividades([]);
    } finally {
      setLoadingActividades(false);
    }
  };

  const cargarTareasPadre = async () => {
    try {
      // Cargar tareas de la actividad que pueden ser padres
      if (actividadId) {
        const response = await eventosGanttService.tareas.listByActividad(actividadId, {
          tarea_padre: 'null' // Solo tareas padre (sin tarea_padre)
        });
        setTareasPadre(response.data.results || response.data);
      }
    } catch (error) {
      console.error('Error al cargar tareas padre:', error);
    }
  };

  const cargarTableros = async () => {
    try {
      setLoadingTableros(true);
      const response = await kanbanService.getTableros();
      const tablerosData = Array.isArray(response) ? response : (response.results || response || []);

      // Filtrar solo tableros públicos (is_private = false)
      const tablerosPublicos = tablerosData.filter(tablero => !tablero.is_private);

      console.log('📊 Tableros cargados:', tablerosPublicos);
      setTableros(tablerosPublicos);
    } catch (error) {
      console.error('Error al cargar tableros:', error);
      setTableros([]);
    } finally {
      setLoadingTableros(false);
    }
  };

  const cargarRecursos = async (actividadId) => {
    try {
      if (!actividadId) {
        console.log('❌ No hay actividadId');
        setRecursos([]);
        return;
      }

      console.log('🔄 Cargando recursos para actividad:', actividadId);
      setLoadingRecursos(true);

      // Obtener la actividad para saber qué partida tiene asignada
      const actividadResponse = await eventosGanttService.actividades.retrieve(actividadId);
      const actividad = actividadResponse.data || actividadResponse;
      console.log('📋 Actividad obtenida:', actividad);

      if (!actividad.presupuesto_costo_partida) {
        console.log('⚠️ La actividad no tiene presupuesto_costo_partida asignada');
        setRecursos([]);
        return;
      }

      console.log('🎯 Partida encontrada:', actividad.presupuesto_costo_partida);

      // Obtener los recursos de la partida
      const response = await onebaseService.presupuestoCostoService.getRecursos(actividad.presupuesto_costo_partida);
      const recursosData = response.data?.results || response.data || [];
      console.log('✅ Recursos cargados:', recursosData);
      setRecursos(recursosData);
    } catch (error) {
      console.error('❌ Error al cargar recursos:', error);
      setRecursos([]);
    } finally {
      setLoadingRecursos(false);
    }
  };

  const cargarEstadosDelTablero = async (boardId) => {
    try {
      console.log('🔄 Cargando estados para tablero:', boardId);
      const response = await kanbanService.getBoardStatuses(boardId);
      console.log('📋 Respuesta de estados (raw):', response);

      let estados = [];
      if (Array.isArray(response)) {
        estados = response;
      } else if (response && response.statuses) {
        // La API retorna los estados en la propiedad 'statuses'
        estados = response.statuses;
      } else if (response && response.results) {
        estados = response.results;
      } else if (response && typeof response === 'object') {
        // Si es un objeto pero no tiene results o statuses, podría ser directamente los datos
        estados = Array.isArray(response.data) ? response.data : [];
      }

      // Mapear estados para asegurar que tenemos las propiedades correctas
      const estadosMapeados = estados.map(estado => ({
        id: estado.id,
        name: estado.name || estado.titulo || estado.nombre || `Estado ${estado.id}`,
        // Mantener otras propiedades si existen
        ...estado
      }));

      console.log('✅ Estados procesados:', estadosMapeados);
      setEstadosKanban(estadosMapeados);
    } catch (error) {
      console.error('Error al cargar estados del tablero:', error);
      toast.error('No se pudieron cargar los estados del tablero');
      setEstadosKanban([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['orden', 'porcentaje_avance', 'horas_estimadas'].includes(name)
        ? (value ? parseFloat(value) : '')
        : value
    }));
  };

  const validateForm = () => {
    if (!formData.titulo.trim()) {
      toast.error('El título es requerido');
      return false;
    }

    if (!formData.actividad) {
      toast.error('Debe seleccionar una actividad');
      return false;
    }

    if (!formData.fecha_inicio) {
      toast.error('La fecha de inicio es requerida');
      return false;
    }

    if (!formData.fecha_termino) {
      toast.error('La fecha de término es requerida');
      return false;
    }

    if (new Date(formData.fecha_termino) < new Date(formData.fecha_inicio)) {
      toast.error('La fecha de término debe ser igual o posterior a la fecha de inicio');
      return false;
    }

    if (formData.porcentaje_avance < 0 || formData.porcentaje_avance > 100) {
      toast.error('El porcentaje de avance debe estar entre 0 y 100');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const dataToSend = {
        evento: eventoId,  // Requerido por el modelo TareaEvento
        actividad: formData.actividad || actividadId,  // Usar el del form si está disponible
        ...formData,
        // Convertir strings vacíos a null
        usuario_asignado: formData.usuario_asignado || null,
        equipo_asignado: formData.equipo_asignado || null,
        tarea_padre: formData.tarea_padre || null,
        horas_estimadas: formData.horas_estimadas || null,
        estado_kanban: formData.estado_kanban || null,  // Usar estado_kanban (puede ser null si no hay tablero)
      };

      let tareaCreada;
      if (tarea) {
        await eventosGanttService.tareas.update(tarea.id, dataToSend);
        toast.success('Tarea actualizada correctamente');
      } else {
        const response = await eventosGanttService.tareas.create(dataToSend);
        tareaCreada = response.data || response;

        // Si se seleccionó un estado Kanban, el signal del backend automáticamente
        // creará la TareaKanban y la vinculará con la TareaEvento
        if (formData.estado_kanban) {
          console.log('✅ TareaEvento creada con estado_kanban:', formData.estado_kanban);
          console.log('🔗 El signal auto_create_tarea_kanban creará automáticamente la TareaKanban');
          toast.success('Tarea creada correctamente y vinculada con Kanban');
        } else {
          toast.success('Tarea creada correctamente');
        }
      }

      window.dispatchEvent(new CustomEvent('tareaActualizada'));
      onSave();
      onClose();
    } catch (error) {
      console.error('Error al guardar tarea:', error);
      const errorMsg = error.response?.data?.detail ||
                      Object.values(error.response?.data || {}).flat().join(', ') ||
                      'Error al guardar la tarea';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content tarea-evento-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>
            {tarea ? '✏️ Editar Tarea' : '➕ Nueva Tarea'}
          </h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Título */}
          <div className="form-group">
            <label htmlFor="titulo">Título de la Tarea *</label>
            <input
              id="titulo"
              type="text"
              name="titulo"
              value={formData.titulo}
              onChange={handleChange}
              placeholder="Ej: Crear mockups"
              maxLength={255}
              style={{ boxSizing: 'border-box' }}
            />
            <small>{formData.titulo.length}/255</small>
          </div>

          {/* Descripción */}
          <div className="form-group">
            <label htmlFor="descripcion">Descripción</label>
            <textarea
              id="descripcion"
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              placeholder="Descripción opcional de la tarea"
              rows={3}
              style={{ boxSizing: 'border-box' }}
            />
          </div>

          {/* Actividad */}
          <div className="form-group">
            <label htmlFor="actividad">Actividad *</label>
            <select
              id="actividad"
              value={formData.actividad || ''}
              onChange={(e) => {
                const actividad = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  actividad: actividad
                }));
              }}
              style={{ boxSizing: 'border-box' }}
              disabled={loadingActividades}
            >
              <option value="">Seleccionar actividad</option>
              {actividades.map(actividad => (
                <option key={actividad.id} value={actividad.id}>
                  {actividad.titulo}
                </option>
              ))}
            </select>
            {loadingActividades && <small>Cargando actividades...</small>}
          </div>

          {/* Tablero Kanban */}
          <div className="form-group">
            <label htmlFor="tablero">Tablero Kanban (Opcional)</label>
            <select
              id="tablero"
              value={tableroSeleccionado}
              onChange={(e) => setTableroSeleccionado(e.target.value)}
              style={{ boxSizing: 'border-box' }}
              disabled={loadingTableros}
            >
              <option value="">Sin tablero Kanban</option>
              {tableros.map(tablero => (
                <option key={tablero.id} value={tablero.id}>
                  {tablero.nombre || tablero.name}
                </option>
              ))}
            </select>
            {loadingTableros && <small>Cargando tableros...</small>}
          </div>

          {/* Usuario Asignado */}
          <div className="form-group">
            <label htmlFor="usuario_asignado">Usuario Asignado (Opcional)</label>
            <select
              id="usuario_asignado"
              value={formData.usuario_asignado || ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  usuario_asignado: value ? parseInt(value) : ''
                }));
              }}
              style={{ boxSizing: 'border-box' }}
            >
              <option value="">Sin asignar</option>
              {usuarios.map(usuario => (
                <option key={usuario.id} value={usuario.id}>
                  {usuario.full_name || usuario.username} {usuario.email ? `(${usuario.email})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Equipo Asignado */}
          <div className="form-group">
            <label htmlFor="equipo_asignado">Equipo Asignado (Opcional)</label>
            <select
              id="equipo_asignado"
              value={formData.equipo_asignado || ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  equipo_asignado: value ? parseInt(value) : ''
                }));
              }}
              style={{ boxSizing: 'border-box' }}
            >
              <option value="">Sin equipo</option>
              {equipos.map(equipo => (
                <option key={equipo.id} value={equipo.id}>
                  {equipo.nombre}
                </option>
              ))}
            </select>
            <small style={{ color: '#6b7280' }}>
              💡 Si asignas un equipo, todos los miembros del equipo pueden registrar tiempo en esta tarea
            </small>
          </div>

          {/* Recursos del Presupuesto (multiselect) */}
          {formData.actividad && (
            <div className="form-group">
              <label htmlFor="presupuesto_costo_recursos">Recursos del Presupuesto (Opcional)</label>
              <select
                id="presupuesto_costo_recursos"
                multiple
                value={formData.presupuesto_costo_recursos}
                onChange={(e) => {
                  // Obtener todos los valores seleccionados
                  const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                  setFormData(prev => ({
                    ...prev,
                    presupuesto_costo_recursos: selectedOptions
                  }));
                }}
                style={{
                  boxSizing: 'border-box',
                  minHeight: '120px',
                  padding: '0.5rem'
                }}
                disabled={loadingRecursos || recursos.length === 0}
              >
                {recursos.map(recurso => (
                  <option key={recurso.id} value={recurso.id}>
                    {recurso.nombre || `Recurso #${recurso.id}`}
                  </option>
                ))}
              </select>
              {loadingRecursos && <small>⏳ Cargando recursos...</small>}
              {!loadingRecursos && recursos.length === 0 && (
                <small style={{ color: '#f59e0b' }}>
                  ℹ️ La actividad no tiene una partida de presupuesto asignada o la partida no tiene recursos
                </small>
              )}
              {!loadingRecursos && recursos.length > 0 && (
                <small>
                  ✅ {recursos.length} recurso{recursos.length > 1 ? 's' : ''} disponible{recursos.length > 1 ? 's' : ''}
                  {formData.presupuesto_costo_recursos.length > 0 && (
                    <span style={{ color: '#69B32D', fontWeight: '600', marginLeft: '0.5rem' }}>
                      ({formData.presupuesto_costo_recursos.length} seleccionado{formData.presupuesto_costo_recursos.length > 1 ? 's' : ''})
                    </span>
                  )}
                  <br />
                  <span style={{ fontSize: '0.85em', color: '#6b7280' }}>
                    💡 Mantén presionado Ctrl (Windows) o Cmd (Mac) para seleccionar múltiples recursos
                  </span>
                </small>
              )}
            </div>
          )}

          {/* Estado (desde Kanban) */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="estado_kanban">
                Estado Kanban
                {tableroSeleccionado && (
                  <span style={{ color: '#69B32D', fontWeight: '600', marginLeft: '0.5rem' }}>
                    (del Tablero)
                  </span>
                )}
              </label>
              <select
                id="estado_kanban"
                name="estado_kanban"
                value={formData.estado_kanban}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    estado_kanban: e.target.value ? parseInt(e.target.value) : ''
                  }));
                }}
                style={{ boxSizing: 'border-box' }}
              >
                <option value="">Sin estado Kanban</option>
                {tableroSeleccionado && estadosKanban.length > 0 ? (
                  // Mostrar SOLO estados del Kanban cuando hay tablero seleccionado
                  estadosKanban.map(estado => (
                    <option key={estado.id} value={estado.id}>
                      {estado.name || estado.titulo || `Estado ${estado.id}`}
                    </option>
                  ))
                ) : tableroSeleccionado ? (
                  // Mostrar mensaje si hay tablero pero sin estados
                  <option value="">Cargando estados...</option>
                ) : (
                  // Si no hay tablero, campo opcional
                  <option value="">Seleccionar un tablero primero</option>
                )}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="porcentaje_avance">Avance (%)</label>
              <input
                id="porcentaje_avance"
                type="number"
                name="porcentaje_avance"
                value={formData.porcentaje_avance}
                onChange={handleChange}
                min="0"
                max="100"
                style={{ boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Fechas */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fecha_inicio">
                Fecha Inicio *
                {actividadSeleccionadaData && (
                  <span style={{ fontSize: '0.85em', color: '#6b7280', marginLeft: '0.5rem' }}>
                    (Rango: {actividadSeleccionadaData.fecha_inicio} - {actividadSeleccionadaData.fecha_termino})
                  </span>
                )}
              </label>
              <input
                id="fecha_inicio"
                type="date"
                name="fecha_inicio"
                value={formData.fecha_inicio}
                onChange={handleChange}
                min={actividadSeleccionadaData?.fecha_inicio || ''}
                max={actividadSeleccionadaData?.fecha_termino || ''}
                style={{ boxSizing: 'border-box' }}
              />
              {actividadSeleccionadaData && (
                <small style={{ color: '#6b7280' }}>
                  📅 Debe estar entre {actividadSeleccionadaData.fecha_inicio} y {actividadSeleccionadaData.fecha_termino}
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="fecha_termino">
                Fecha Término *
                {actividadSeleccionadaData && (
                  <span style={{ fontSize: '0.85em', color: '#6b7280', marginLeft: '0.5rem' }}>
                    (Rango: {actividadSeleccionadaData.fecha_inicio} - {actividadSeleccionadaData.fecha_termino})
                  </span>
                )}
              </label>
              <input
                id="fecha_termino"
                type="date"
                name="fecha_termino"
                value={formData.fecha_termino}
                onChange={handleChange}
                min={actividadSeleccionadaData?.fecha_inicio || ''}
                max={actividadSeleccionadaData?.fecha_termino || ''}
                style={{ boxSizing: 'border-box' }}
              />
              {actividadSeleccionadaData && (
                <small style={{ color: '#6b7280' }}>
                  📅 Debe estar entre {actividadSeleccionadaData.fecha_inicio} y {actividadSeleccionadaData.fecha_termino}
                </small>
              )}
            </div>
          </div>

          {/* Horas */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="horas_estimadas">Horas Estimadas</label>
              <input
                id="horas_estimadas"
                type="number"
                name="horas_estimadas"
                value={formData.horas_estimadas}
                onChange={handleChange}
                placeholder="Ej: 8"
                step="0.5"
                min="0"
                style={{ boxSizing: 'border-box' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="orden">Orden</label>
              <input
                id="orden"
                type="number"
                name="orden"
                value={formData.orden}
                onChange={handleChange}
                min="0"
                style={{ boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Tarea Padre (para subtareas) */}
          <div className="form-group">
            <label htmlFor="tarea_padre">Tarea Padre (si es subtarea)</label>
            <select
              id="tarea_padre"
              name="tarea_padre"
              value={formData.tarea_padre}
              onChange={handleChange}
              style={{ boxSizing: 'border-box' }}
            >
              <option value="">Sin tarea padre</option>
              {tareasPadre.map(t => (
                <option key={t.id} value={t.id}>
                  {t.titulo}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TareaEventoModal;
